import { IconDownload } from "@tabler/icons-react";
import NormalSearch from "../../../components/common/NormalSearch";
import DropdownFilter from "../../../components/common/DropdownFilter";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ListView from "../../../components/common/ListView";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useUpdateProductItemStatus } from "../../../apis/productApis";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import { useFetchProductItemInventoryData } from "../../../apis/productManifestApis";
import { formatDateTime } from "../../../utilities/helper";
import { useLanguage, T } from "../../../context/LanguageContext";
export default function ItemInventory({ 
  warehouseId,
  searchQuery,
  portalContainer
}: { 
  warehouseId?: string;
  searchQuery?: string;
  portalContainer?: HTMLDivElement | null;
}) {
  const { t } = useLanguage();
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const { allowed: canUpdateProduct } = usePermission("products", "update");
  const [localSearch, setLocalSearch] = useState<string>("");
  const searchFor = searchQuery !== undefined ? searchQuery : localSearch;
  const setSearchFor = searchQuery !== undefined ? () => {} : setLocalSearch;
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionModal, setActionModal] = useState<boolean>(false);

  const { mutate: updateProductStatus, isPending: statusPending } =
    useUpdateProductItemStatus();

  const handleToggleStatus = (item: any) => {
    setSelectedItem(item);
    setActionModal(true);
  };

  const confirmUpdateStatus = async () => {
    if (!canUpdateProduct) {
      toast.error(t("You do not have permission to update items."));
      return;
    }
    const newStatus = selectedItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    updateProductStatus(
      { id: selectedItem?.id, status: newStatus },
      {
        onSuccess: () => {
          refetch();
          toast.success(t("Status updated successfully"));
          setActionModal(false);
        },
        onError: (error) => {
          toast.error(t(`${error}`));
        },
      },
    );
  };
  const debouncedSearchFor = useDebounce(searchFor, 550);
  const { data, isLoading, isFetching, error, refetch } =
    useFetchProductItemInventoryData(
      pageNo,
      limit,
      debouncedSearchFor,
      undefined,
      warehouseId,
    );

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearchFor, warehouseId]);

  useEffect(() => {
    setPageNo(1);
  }, [warehouseId]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const [columnsData, setColumnsData] = useState([
    { text: "S/N", value: "sn", isShow: true },
    { text: "Item Type", value: "itemType", isShow: true },
    { text: "Bin Location", value: "shelveID", isShow: true },
    { text: "Item", value: "itemName", isShow: true },
    { text: "SKU", value: "sku", isShow: true },
    { text: "Storage Type", value: "storageType", isShow: true },
    { text: "Capacity", value: "capacity", isShow: true },
    { text: "Avl. Qty.", value: "qty", isShow: true },
    { text: "Inventory Type", value: "inventoryType", isShow: true },
    // { text: "Warehouse", value: "warehouse", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Expiry Date", value: "expiryDate", isShow: true },
    { text: "Last Updated", value: "updatedAt", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "sn":
          return {
            label: t("#"),
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "itemType":
          return {
            label: t("Item Type"),
            render: (c: any, index: any) => (
              <div className="flex">
                <span
                  key={index}
                  title={c.itemName}
                  className={` px-3 py-1 rounded-full text-white  ${
                    c?.storage !== "BIN" ? "bg-green-500  " : "bg-blue-500"
                  } "`}
                >
                  {c?.storage !== "BIN" ? "B2C" : "B2B"}
                </span>
              </div>
            ),
          };
        case "shelveID":
          return {
            label: t("Location"),
            render: (c: any) => (
              <div className="flex flex-col items-center space-x-3">
                <img
                  alt=""
                  src={
                    c?.binLocationBarcode
                      ? c.binLocationBarcode
                      : "/img/bin-location-placeholder.png"
                  }
                  className="object-cover object-center w-full h-11"
                />
                <span
                  title={c.itemName}
                  className="text-[13px] font-semibold text-[#131327]/70"
                >
                  {c.locationName ? c.locationName : ""}
                </span>
              </div>
            ),
          };
        case "itemName":
          return {
            label: t("Items"),
            render: (c: any) => (
              <div className="flex items-center space-x-3 truncate max-w-xs">
                <img
                  alt={c.itemName}
                  src={c.image || "/img/product-placeholder.png"}
                  className="size-12 rounded-md object-cover object-center "
                />
                <span title={c.itemName} className="truncate w-20">
                  {c.itemName}
                </span>
              </div>
            ),
          };

        case "sku":
          return {
            label: t("SKU"),
            render: (c: any) => c?.sku,
          };
        case "storageType":
          return {
            label: t("Storage Type"),
            render: (c: any) => c.locationType || "BIN",
          };
        case "capacity":
          return {
            label: t("Capacity"),
            render: (c: any) => c.capacity || "---",
          };
        case "qty":
          return {
            label: t("Avl. Qty"),
            render: (c: any) => c.quantity ?? "-",
          };

        case "inventoryType":
          return {
            label: t("Inventory Type"),
            render: (c: any) => {
              const type = c.isReserved ? t("Reserved") : t("Received");
              const bgColor = c.isReserved 
                ? "bg-amber-100 text-amber-800 border-amber-200" 
                : "bg-emerald-100 text-emerald-800 border-emerald-200";
              return (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${bgColor}`}>
                  {type}
                </span>
              );
            }
          };

        case "warehouse":
          return {
            label: t("Warehouse"),
            render: (c: any) => c.warehouseName,
          };

        case "expiryDate":
          return {
            label: t("Expiry Date"),
            render: (c: any) => {
              if (!c?.expiryDate) return <div></div>;
              return <div>{c?.expiryDate}</div>;
            },
          };

        case "status":
          return {
            label: t("Expiry Status"),
            render: (c: any) => {
              if (!c?.expiryDate)
                return <div style={{ color: "gray" }}>{t("No Expiry")}</div>;

              const now = new Date();
              const expiry = new Date(c.expiryDate);
              // Normalize to end of day so it doesn't expire at 00:00 UTC
              expiry.setHours(23, 59, 59, 999);

              const diffMs = expiry.getTime() - now.getTime();

              // 1. Actually Expired (Past the end of the day)
              if (diffMs <= 0) {
                return (
                  <div style={{ color: "red", fontWeight: "bold" }}>
                    {t("Expired")}
                  </div>
                );
              }

              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

              // 2. Expiring Today
              if (diffHours < 24) {
                return (
                  <div style={{ color: "red", fontWeight: "bold" }}>
                    {t("Expires in")} {diffHours} {t("hours")}
                  </div>
                );
              }

              // 3. Expiring Soon
              if (diffDays <= 7) {
                return (
                  <div style={{ color: "orange", fontWeight: "bold" }}>
                    {t("Expires in")} {diffDays} {t("days")}
                  </div>
                );
              }

              // 4. Healthy
              return (
                <div style={{ color: "green" }}>{t("Active")} ({diffDays} {t("days")})</div>
              );
            },
          };
        // case "actions":
        //   return {
        //     label: "Actions",
        //     render: (item: any) => (
        //       <div className="text-center">
        //         <button
        //           // onClick={() => navigate(`/add-item/${item.id}`)}
        //           className="p-2 border rounded-full bg-[#F7F6F2]"
        //         >
        //           <IconEye size={22} />
        //         </button>
        //       </div>
        //     ),
        //   };
        case "updatedAt":
          return {
            label: t("Last Updated"),
            render: (c: any) => formatDateTime(c.updatedAt),
          };

        default:
          return { label: "", render: () => null };
      }
    });

  const actionControls = (
    <>
      {/* Search */}
      {searchQuery === undefined && (
        <NormalSearch
          value={searchFor}
          onChange={(e) => {
            setSearchFor(e.target.value);
            setPageNo(1);
          }}
        />
      )}

      {/* Export/Download */}
      <button
        className="flex items-center justify-center p-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-md transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150"
        title={t("Export")}
      >
        <IconDownload size={17} stroke={2.5} />
      </button>

      {/* Entries Limit Selector */}
      {data && data.totalPages * limit > 10 && (
        <EntriesSelector
          value={limit}
          options={[
            10,
            ...(data.totalPages * limit > 10 ? [20] : []),
            ...(data.totalPages * limit > 20 ? [30] : []),
            ...(data.totalPages * limit > 50 ? [50] : []),
            ...(data.totalPages * limit > 100 ? [100] : []),
          ]}
          onChange={(val) => {
            setLimit(val);
            setPageNo(1);
          }}
        />
      )}

      {/* Columns Hide/Show Filter */}
      <ColumnsFilter columnsData={columnsData.map(c => ({...c, text: t(c.text)}))} setHeadings={setHeadings} />
    </>
  );

  return (
    <>
      {portalContainer && createPortal(actionControls, portalContainer)}

      {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
      <div
        className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-visible mb-6"
        style={{ minHeight: "150px" }}
      >
        {/* Table Container (Joined) */}
        <div className="">
          {isLoading ? (
            <TableSkeleton
              columns={columnsData.filter((c) => c.isShow).map((c) => t(c.text))}
              rows={6}
            />
          ) : data?.inventory?.length ? (
            <ListView data={data?.inventory} columns={columns} />
          ) : (
            <NoDataFound />
          )}
        </div>
      </div>

      <div className="flex justify-center items-center py-4">
        {data?.totalPages > 1 && (
          <Pagination
            currentPage={pageNo}
            totalPages={data?.totalPages}
            fetchData={handlePaginationFetch}
          />
        )}
      </div>
      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title={t("Confirmation Required")}
        subtitle={t("Are you sure you want to perform this action?")}
        img={
          selectedItem?.status === "ACTIVE"
            ? "/img/inactive.svg"
            : "/img/active.svg"
        }
        showFooter={true}
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={confirmUpdateStatus}
        isPending={statusPending}
        size="md"
      />
    </>
  );
}
